/**
 * The session's automatic strip print, shared by the two places that send it:
 * Select when the Edit page is off, and Edit's Continue when it is on (Select
 * defers the print to Edit then). One copy keeps both paths on the same printer
 * choice — parallel round-robin or frame routing — and the same session context
 * in printeventlogs.
 */

import cogoToast from '@successtar/cogo-toast'
import type { TFunction } from 'i18next'
import { isDigitalMode } from '@renderer/lib/runtime'
import { usePrintStore } from '@renderer/stores/printStore'
import { initiatePrint, type PrintEventContext } from './printService'

/** Safety net only: a print normally reaches the driver in under 2s. */
export const SESSION_PRINT_TIMEOUT_MS = 15000

const PAGE_LABELS = {
  select: { tag: '[Select]', sendingMessage: 'Sending print job' },
  // Kept distinct so activity logs still tell the two paths apart.
  edit: { tag: '[Edit]', sendingMessage: 'Sending print job for final strip with stickers' }
} as const

export interface SessionPrintRequest {
  /** The page sending the print; picks its log lines and toast keys. */
  page: keyof typeof PAGE_LABELS
  stripBlob: Blob
  usePrint: boolean
  copies: number
  frameId: string
  isOnline: boolean
  /** Ties the job to its session in printeventlogs. */
  context: PrintEventContext
  /** One automatic print per session: set when the job is handed off. */
  sentRef: { current: boolean }
  logActivity: (message: string, details?: Record<string, unknown>) => void
  t: TFunction
}

function printSkipReason(isPrintingEnabled: boolean, req: SessionPrintRequest): string | null {
  if (!isPrintingEnabled) return 'printing disabled on this booth'
  if (!req.usePrint) return 'session has no print'
  if (req.copies <= 0) return 'session has 0 copies'
  if (req.sentRef.current) return 'already sent for this session'
  return null
}

/**
 * Sends the strip to the printer, waiting until the driver has it (or the
 * safety timeout passes).
 *
 * @returns whether a print was attempted, which drives the minimum result display.
 */
export async function printSessionStrip(req: SessionPrintRequest): Promise<boolean> {
  const { page, stripBlob, copies, frameId, isOnline, logActivity, t } = req
  const { tag, sendingMessage } = PAGE_LABELS[page]
  const {
    isPrintingEnabled,
    secondaryPrinter,
    tertiaryPrinter,
    useSumatraPDF,
    secondaryUseSumatraPDF,
    tertiaryUseSumatraPDF,
    getNextParallelPrinter,
    getPrinterForFrame
  } = usePrintStore.getState()

  // Digital web booths have no printer — skip the whole print step (no toast, no
  // print-js dialog). PrintUtils.sendToPrinter also no-ops in digital as defense.
  if (isDigitalMode()) return false

  // Every other skip is logged with its reason. These returns used to be silent,
  // and they leave no printeventlogs row either, so "sometimes it doesn't print"
  // (B33) could not be told apart from a guest who bought no print.
  const skipReason = printSkipReason(isPrintingEnabled, req)
  if (skipReason) {
    logActivity('Session print skipped', {
      reason: skipReason,
      page,
      copies,
      usePrint: req.usePrint,
      isPrintingEnabled,
      // A print was wanted but there are no copies: the B33 signature.
      ...(req.usePrint && isPrintingEnabled && copies <= 0 ? { level: 'warning' } : {})
    })
    return false
  }
  req.sentRef.current = true // Prevent duplicate print jobs

  // Check if parallel printing mode is enabled (any printer has purpose='parallel')
  const isParallelModeEnabled =
    secondaryPrinter?.purpose === 'parallel' || tertiaryPrinter?.purpose === 'parallel'

  // Get printer config: use parallel printing if enabled, otherwise use frame-based routing
  const printerConfig = isParallelModeEnabled
    ? getNextParallelPrinter()
    : getPrinterForFrame(frameId)

  if (!printerConfig) {
    console.warn(`${tag} No printer available for printing`)
    logActivity('No printer available for printing', {
      isParallelModeEnabled,
      frameId
    })
    return true
  }

  const { printerName, settings } = printerConfig
  // Determine which printer type is being used to get the correct useSumatraPDF value
  const isSecondaryPrinter = secondaryPrinter?.printerName === printerName
  const isTertiaryPrinter = tertiaryPrinter?.printerName === printerName
  const usePrinterDriverDefaults = isTertiaryPrinter
    ? tertiaryUseSumatraPDF
    : isSecondaryPrinter
      ? secondaryUseSumatraPDF
      : useSumatraPDF

  logActivity(sendingMessage, {
    copies,
    printerName,
    pageSize: settings.pageSize,
    orientation: settings.orientation,
    margins: settings.margins,
    dpi: settings.dpi,
    scaleFactor: settings.scaleFactor,
    printBackground: settings.printBackground,
    printResolutionScale: settings.printResolutionScale,
    frameId,
    isParallelMode: isParallelModeEnabled,
    usePrinterDriverDefaults
  })

  try {
    const printPromise = initiatePrint(
      stripBlob,
      printerName,
      copies,
      settings,
      isOnline,
      undefined,
      'session',
      req.context
    )
    // The safety timeout below can win the race while main is still
    // retrying, and navigation continues without waiting. Report the real
    // outcome whenever it lands so a session that printed nothing never
    // passes silently — and so a late rejection is never unhandled.
    let navigationTimedOut = false
    printPromise.catch((error) => {
      if (!navigationTimedOut) return // the catch block below reports it
      const lateMsg = error instanceof Error ? error.message : String(error)
      cogoToast.error(`${t(`${page}.printFailed`, 'Failed to print')}: ${lateMsg}`)
      logActivity('Print failed after navigation continued', {
        printerName,
        copies,
        error: lateMsg,
        level: 'warning'
      })
    })
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        navigationTimedOut = true
        reject(new Error('Print initialization timed out'))
      }, SESSION_PRINT_TIMEOUT_MS)
    })

    await Promise.race([printPromise, timeoutPromise])
    // Only now is the job actually with the driver. Announcing success
    // up-front showed a green "Printing Initialized" on every failed
    // print, so the operator saw success then an error toast.
    cogoToast.success(t(`${page}.printInitiated`, 'Printing Initialized'))
  } catch (error) {
    // Only suppress if offline and error is network-related, or if timeout
    const errorMsg = error instanceof Error ? error.message : String(error)
    const isTimeout = errorMsg.includes('timed out')
    const isNetworkError =
      !isOnline && (errorMsg.includes('network') || errorMsg.includes('fetch'))

    // Log timeout but don't show error to user (print may still succeed in background)
    if (isTimeout) {
      console.warn(`${tag} Print initialization timed out, continuing navigation`)
      logActivity('Print initialization timed out, continuing navigation', {
        printerName,
        copies
      })
    } else if (!isNetworkError || isOnline) {
      cogoToast.error(`${t(`${page}.printFailed`, 'Failed to print')}: ${errorMsg}`)
    }
  }
  return true
}
